import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Form, FormField, FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SchoolAddressFieldset } from '@/shared/components/forms/SchoolAddressFieldset';
import { PhotoUploadField } from '@/shared/components/forms/PhotoUploadField';
import { DatePickerField } from '@/shared/components/forms/DatePickerField';
import { useWasteCollectionWithForm } from '../hooks/useWasteCollectionWithForm';
import { useFormNavigation, useConfirmationDialog } from '@/shared/hooks';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { generateMinPickupDate } from '../validation/waste-collection.schemas';
import { FaRotate } from 'react-icons/fa6';
import { DOCUMENT_LINKS } from '@/constants/config';
import { erorileFormularului } from '../validation/etichete-erori';
import { CHEIE_EROARE_PRAG, MESAJ_PRAG_NEATINS, TEXT_PRAGURI, pragAtins } from '../validation/praguri-comanda';
import { handleInvalidSubmit } from '@/shared/utils';
import { MESAJ_ADRESA_SCOLII_INCOMPLETA } from '../validation/adresa-colectare';

/**
 * WasteCollectionFormPage - Waste collection request form
 *
 * Refactored with Context7-validated patterns:
 * - Zod validation (automatic number coercion, date validation, photo array validation)
 * - react-hook-form (watch + useEffect + setValue for address toggle)
 * - shadcn Form components (consistent UI, accessibility)
 * - Reusable components (PhotoUploadField, DatePickerField, SchoolAddressFieldset)
 * - Design system utilities (form-field-row, form-label-bold, etc.)
 *
 * Reduced from 315 lines to ~200 lines (37% reduction)
 */
export default function WasteCollectionFormPage() {
  const { form, onSubmit, loading, isDirty, isError, refetch, adresaScoliiIncompleta } = useWasteCollectionWithForm();

  // Navigation blocking when form has unsaved changes
  const navigationDialog = useConfirmationDialog({
    onConfirm: () => blocker.proceed?.(),
    onCancel: () => blocker.reset?.(),
  });

  const { blocker } = useFormNavigation({
    isDirty,
    onNavigationAttempt: navigationDialog.show,
  });

  // Submission confirmation
  const submitDialog = useConfirmationDialog({
    onConfirm: async () => {
      // `false` = serverul a respins trimiterea: dialogul RAMANE deschis, ca utilizatorul sa poata
      // reincerca din el. Inainte ramanea deschis doar pentru ca `onSubmit` arunca — iar aruncarea
      // aceea ajungea in telemetrie ca promisiune respinsa netratata.
      let trimis = true;
      await form.handleSubmit(async (data) => {
        console.log('[WasteCollectionForm] Dialog confirmed, submitting data:', data);
        console.log('[WasteCollectionForm] Form state:', {
          isDirty,
          isValid: form.formState.isValid,
          errors: form.formState.errors,
        });
        trimis = await onSubmit();
        // Al doilea argument nu e optional in practica: fara el, un submit respins de Zod NU
        // executa nimic — dialogul se inchide si utilizatorul ramane fara niciun semnal.
      }, () => handleInvalidSubmit())();
      return trimis;
    },
  });

  // Watch address toggle for conditional rendering
  const sameAsSchool = form.watch('pickupAddressSameWithSchoolAddress');

  // Calculate minimum pickup date (15 days from now)
  const minPickupDate = generateMinPickupDate();

  /* Rezumatul de erori din capul formularului. `erorileFormularului` coboara si
     in grupul `pickupAddress` si inlocuieste numele tehnic al campului cu
     eticheta lui din formular. */
  const erori = erorileFormularului(form.formState.errors);

  /* Eroarea de grup a pragurilor stă pe `root.cantitati`, pe care validarea unui câmp nu o
     atinge. Deci se șterge de aici, în clipa în care o cantitate atinge pragul — altfel caseta
     de sus ar rămâne cu o eroare deja corectată până la următorul «Trimite Formularul». */
  const [deeeKg, dbaKg, becuriSiNeoaneKg] = form.watch(['deeeKg', 'dbaKg', 'becuriSiNeoaneKg']);
  const arePragNeatins = Boolean(form.formState.errors.root?.cantitati);
  useEffect(() => {
    if (arePragNeatins && pragAtins({ deeeKg, dbaKg, becuriSiNeoaneKg })) {
      form.clearErrors(CHEIE_EROARE_PRAG);
    }
  }, [arePragNeatins, deeeKg, dbaKg, becuriSiNeoaneKg, form]);

  return (
    <div className="flex-1 flex flex-col md:overflow-hidden">
      {/* `pt-10` sub `md` rezerva locul hamburgerului flotant din `AuthenticatedLayout` (16..68 px
          la 360): h1 statea la y=40..104, sub buton (masurat S1). +40 px il duc la 80, cu 12 px
          liberi sub buton, ca in `CadruPasVideo`/`ProtocolPage` (1a74ecc4). Pe wrapper, nu pe
          `ecotic-form-card`, ca sa nu inlocuiasca padding-ul cardului. De la `md` neschimbat. */}
      <div className="flex-1 pr-4 pt-10 ecotic-form-scrollbar md:overflow-y-auto md:pt-0">
        {/* Scrollerul e DOAR de la `md` in sus. Sub `md` formularul curge in
            pagina: un container derulabil pe telefon inseamna ca degetul misca
            altceva decat crede utilizatorul, si ascunde bara de actiuni sub el.
            Masurat la 360px: containerul chiar derula (scrollHeight > clientHeight). */}
        <div className="ecotic-form-card space-y-6">
          <h1 className="text-2xl font-bold text-foreground">
            Formular de preluare a deșeurilor
          </h1>

          {/* Rezumatul erorilor de validare. Era marcat „Debug: Show validation
              errors" in cod, dar e randat utilizatorului in productie de la
              inceput — deci nu e cod de depanare, e interfata, si a fost tratat
              ca atare. Trei lucruri reparate, niciunul de forma:

               1. afisa CHEIA din schema (`becuriSiNeoaneKg`,
                  `dataOrientativaColectare`), un nume care nu apare nicaieri pe
                  ecran. Acum afiseaza eticheta campului din formular;
               2. erorile de adresa veneau grupate sub `pickupAddress`, ca obiect
                  fara `message` — o singura linie, cu mesaj gol, indiferent cate
                  subcampuri erau gresite. Acum se coboara in grup;
               3. mesajul nu se afisa NICIODATA. `getErrorMessage` intoarce
                  `.message` doar pentru un `Error` real (`error instanceof Error`),
                  iar un `FieldError` de la react-hook-form e obiect simplu — deci
                  fiecare rand iesea „Eroare necunoscuta", pentru orice camp.
                  (Masurat pe `998ca59a`; `response-handler.ts` e in curs de
                  schimbare pe alta ramura, dar rezumatul asta nu mai depinde de el.)

              Forma casetei NU se schimba: e o caseta care raporteaza ce s-a
              intamplat deja, deci ramane pe umplutura, fara chenar plin (regula
              lui S1 pentru casetele de dupa actiune). */}
          {isError ? (
            <Alert className="border-error bg-error/10">
              <AlertDescription>
                <div className="text-center">
                  <p className="mb-2 font-semibold text-error">Nu am putut încărca datele formularului</p>
                  <p className="text-error">Verificați conexiunea și încercați din nou.</p>
                  <Button type="button" variant="outline" className="mt-3" onClick={() => refetch()}>
                    <FaRotate className="mr-2" />
                    <span>Încearcă din nou</span>
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
          <>
          {erori.length > 0 && (
            /* `scroll-mt-20` sub `md`: pragurile derulează caseta în capul ecranului, iar acolo
               stă butonul fix al meniului (se termină la y=68). Fără marjă, acoperea „Er" din
               «Erori de validare» — măsurat la 360px. */
            <div id="erori-formular" role="alert" className="scroll-mt-20 md:scroll-mt-0 p-4 bg-error/10 border border-error/30 rounded-lg">
              <h3 className="font-semibold text-error mb-2">Erori de validare:</h3>
              <ul className="list-disc list-inside text-sm text-error space-y-1">
                {erori.map(({ cheie, eticheta, mesaj }) => (
                  <li key={cheie}>
                    <strong>{eticheta}:</strong> {mesaj}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              console.log('[WasteCollectionForm] Form submit event triggered');
              /* Pragurile minime se verifică AICI, înainte de dialogul de confirmare, nu la blur:
                 regula e «cel puțin una din trei», iar o valoare parțială (DEEE 2 în drum spre 150)
                 ar arăta fals o eroare. Fără verificarea asta, serverul refuza cu 409 abia după
                 «Trimite» — vezi `praguri-comanda.ts`. */
              if (!pragAtins(form.getValues())) {
                form.setError(CHEIE_EROARE_PRAG, { type: 'prag', message: MESAJ_PRAG_NEATINS });
                // Caseta de erori e în capul formularului; la 360px butonul e cu ~800px mai jos.
                requestAnimationFrame(() =>
                  document.getElementById('erori-formular')?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
                );
                return;
              }
              form.clearErrors(CHEIE_EROARE_PRAG);
              submitDialog.show();
            }}
          >
            <div className="space-y-4">
              {/* Contact Information */}
              <FormField
                control={form.control}
                name="contactPersonName"
                render={({ field }) => (
                  <div className="form-field-row">
                    <FormLabel className="form-label-bold">Denumire Persoană Contact:</FormLabel>
                    <FormItem className="flex-1 space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          className="form-input-styled"
                          disabled={loading}
                          placeholder="Introduceți numele persoanei de contact"
                        />
                      </FormControl>
                      <FormMessage className="form-message-spacer" />
                    </FormItem>
                  </div>
                )}
              />

              <div className="ecotic-form-row">
                <FormField
                  control={form.control}
                  name="contactPersonEmail"
                  render={({ field }) => (
                    <div className="form-field-row">
                      <FormLabel className="form-label-bold">Email Persoană Contact:</FormLabel>
                      <FormItem className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            className="form-input-styled"
                            disabled={loading}
                            placeholder="email@exemplu.ro"
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </FormItem>
                    </div>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactPersonPhone"
                  render={({ field }) => (
                    <div className="form-field-row">
                      <FormLabel className="form-label-bold">Telefon Persoană Contact:</FormLabel>
                      <FormItem className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            className="form-input-styled"
                            disabled={loading}
                            placeholder="07xxxxxxxxx"
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </FormItem>
                    </div>
                  )}
                />
              </div>

              {/* Pickup Date - NOW EDITABLE! */}
              <Controller
                control={form.control}
                name="dataOrientativaColectare"
                render={({ field, fieldState }) => (
                  <DatePickerField
                    value={field.value}
                    onChange={field.onChange}
                    label="Data Orientativă Colectare:"
                    minDate={minPickupDate}
                    error={fieldState.error?.message}
                    disabled={loading}
                    placeholder="Selectați data (minim 15 zile de la data curentă)"
                  />
                )}
              />

              {/* Address Toggle */}
              <FormField
                control={form.control}
                name="pickupAddressSameWithSchoolAddress"
                render={({ field }) => (
                  <div className="form-field-row items-center">
                    <FormLabel className="form-label-bold">Adresa de Colectare:</FormLabel>
                    <FormItem className="flex-1 space-y-0">
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value === true}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                            disabled={loading}
                          />
                        </FormControl>
                        <FormLabel className="!m-0 flex min-h-11 items-center px-2 font-normal cursor-pointer">
                          Adresa de colectare corespunde cu adresa școlii
                        </FormLabel>
                      </div>
                      {/* Cu bifa pusa campurile de adresa sunt dezactivate: o adresa de scoala fara
                          judet sau oras ar bloca trimiterea fara nicio iesire vizibila. */}
                      {sameAsSchool === true && adresaScoliiIncompleta && (
                        <p role="alert" className="text-small text-error">{MESAJ_ADRESA_SCOLII_INCOMPLETA}</p>
                      )}
                      <FormMessage className="form-message-spacer" />
                    </FormItem>
                  </div>
                )}
              />

              {/* Address Fields - Conditional Rendering */}
              {sameAsSchool !== null && (
                <SchoolAddressFieldset
                  namePrefix="pickupAddress"
                  disabled={sameAsSchool === true || loading}
                />
              )}

              {/* Waste Quantities */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700">Cantități deșeuri</h3>
                {/* Un singur rând pentru toate trei pragurile, nu câte un indiciu pe câmp: regula e
                    «cel puțin una din trei» (direcția S1). Cifrele vin din `praguri-comanda.ts`. */}
                <p className="text-small text-gray-700">{TEXT_PRAGURI}</p>

                <FormField
                  control={form.control}
                  name="deeeKg"
                  render={({ field }) => (
                    <div className="form-field-row">
                      <FormLabel className="form-label-bold">Kilograme DEEE:</FormLabel>
                      <FormItem className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-input-styled"
                            disabled={loading}
                            placeholder="0"
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </FormItem>
                    </div>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dbaKg"
                  render={({ field }) => (
                    <div className="form-field-row">
                      <FormLabel className="form-label-bold">Kilograme DBA:</FormLabel>
                      <FormItem className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-input-styled"
                            disabled={loading}
                            placeholder="0"
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </FormItem>
                    </div>
                  )}
                />

                <FormField
                  control={form.control}
                  name="becuriSiNeoaneKg"
                  render={({ field }) => (
                    <div className="form-field-row">
                      <FormLabel className="form-label-bold">Kilograme Becuri și Neoane:</FormLabel>
                      <FormItem className="flex-1 space-y-0">
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-input-styled"
                            disabled={loading}
                            placeholder="0"
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
                          />
                        </FormControl>
                        <FormMessage className="form-message-spacer" />
                      </FormItem>
                    </div>
                  )}
                />
              </div>

              {/* Photo Upload - NEW COMPONENT! */}
              <Controller
                control={form.control}
                name="photos"
                render={({ field, fieldState }) => (
                  <PhotoUploadField
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    disabled={loading}
                    maxPhotos={10}
                    label="Fotografii Deșeuri:"
                  />
                )}
              />

              {/* Consent Checkbox */}
              <FormField
                control={form.control}
                name="consent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4 bg-white">
                    <FormControl>
                      <Checkbox
                        checked={field.value === true}
                        onCheckedChange={field.onChange}
                        disabled={loading}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="flex min-h-11 items-center cursor-pointer">
                        Prin prezenta confirm că am citit{' '}
                        <a
                          href={DOCUMENT_LINKS.POLITICA_CONFIDENTIALITATE}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          Informarea cu privire la prelucrarea datelor mele cu caracter personal
                        </a>
                        {' '}prezentată pe www.ecotic.ro
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {/* Vezi comentariul din SchoolRequestFormPage: doua butoane `w-full`
                intr-un rand de 296px dau 296 + 16 + 296 = 608px, iar
                `justify-center` le imparte in afara ecranului pe ambele parti.
                Masurat la 360px, build de productie: „Resetare" la left=-132,
                „Trimite Formularul" la right=476. */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                disabled={loading}
                onClick={() => form.reset()}
              >
                <FaRotate className="mr-2" />
                <span>Resetare</span>
              </Button>
              <Button type="submit" className="w-full sm:w-auto" disabled={loading}>
                {loading ? 'Se trimite...' : 'Trimite Formularul'}
              </Button>
            </div>
          </form>
        </Form>
        </>
        )}
        </div> {/* max-w-4xl mx-auto */}
      </div> {/* overflow-y-auto ecotic-form-scrollbar */}

      {/* Navigation confirmation - shown when trying to leave page with unsaved changes */}
      <ConfirmationDialog
        isOpen={navigationDialog.isOpen}
        onClose={navigationDialog.handleCancel}
        onConfirm={navigationDialog.handleConfirm}
        title="Modificări nesalvate"
        description="Aveți modificări nesalvate. Doriți să părăsiți pagina?"
        confirmText="Părăsește pagina"
        cancelText="Rămâi pe pagină"
        variant="destructive"
      />

      {/* Submission confirmation - shown when clicking save button */}
      <ConfirmationDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.handleCancel}
        onConfirm={submitDialog.handleConfirm}
        title="Confirmare trimitere"
        description="Doriți să trimiteți formularul?"
        confirmText="Trimite"
        cancelText="Anulare"
        loading={loading}
      />
    </div>
  );
}
